import { DoneRounded, ExpandMore, ExpandLess, Edit, Close, CheckCircle, Cancel as CancelIcon } from "@mui/icons-material";
import { TableRow, TableCell, Collapse, Box, Grid, Typography, Select, MenuItem, Divider, useTheme, Alert, TextField, Button, CircularProgress } from "@mui/material";
import { BasicButton } from "../../../../../components/BasicButton";
import { OrderDetails } from "../../../../../models/DB";
import { useState, useEffect } from "react";
import { getDeliveryTypeSetup, getOrderPaymentDetails } from "../../../../../utils/customFunctions";
import { OrderItemCard } from "../../../../../components/OrderItemCard";
import PortalService from "../../../../../services/supabase/PortalService";
import { OrderStatusBadgeCompact } from "../../../../../components/OrderStatusBadge";
import { useStoreConfig } from "../../../../../contexts/StoreConfigContext";
import apiClient from "../../../../../services/api/ApiClient";
import { ApiEndpoints } from "../../../../../config/constants";
import { ResponseModel } from "../../../../../models/Response";

interface OrderEditorProps {
    order: OrderDetails;
    open: boolean;
    isEditing: boolean;
    setIsEditing: (isEditing: boolean) => void;
}

export const OrderEditor = ({
    order,
    open,
    isEditing,
    setIsEditing,
}: OrderEditorProps) => {
    const theme = useTheme();
    const { config } = useStoreConfig();
    const { updateOrder } = PortalService();
    const [isLoading, setIsLoading] = useState(false);
    const [orderData, setOrderData] = useState<OrderDetails>({
        ...order,
    });
    const [expandItems, setExpandItems] = useState(false);

    // Refund management state
    const [refundRequests, setRefundRequests] = useState<any[]>([]);
    const [refundActionLoading, setRefundActionLoading] = useState<string | null>(null);
    const [merchantNote, setMerchantNote] = useState('');

    useEffect(() => {
        if (open && (orderData.status === 5 || orderData.status === 6)) {
            fetchRefundRequests();
        }
    }, [open, orderData.status]);

    const fetchRefundRequests = async () => {
        try {
            const response = await apiClient.get<ResponseModel<any[]>>(ApiEndpoints.Refunds.List);
            if (response.data.statusCode === 200 && response.data.data) {
                const orderRefunds = response.data.data.filter((r: any) => r.orderId === order.id);
                setRefundRequests(orderRefunds);
            }
        } catch (e) {
            console.error('Failed to fetch refund requests', e);
        }
    };

    const handleRefundAction = async (refundId: string, action: 'approve' | 'reject') => {
        setRefundActionLoading(refundId);
        try {
            const endpoint = action === 'approve'
                ? ApiEndpoints.Refunds.Approve(refundId)
                : ApiEndpoints.Refunds.Reject(refundId);
            const body = action === 'reject' ? { merchantNote: merchantNote || 'Refund rejected' } : { merchantNote };
            await apiClient.put(endpoint, body);
            await fetchRefundRequests();
        } catch (e) {
            console.error(`Failed to ${action} refund`, e);
        } finally {
            setRefundActionLoading(null);
            setMerchantNote('');
        }
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleSave = async () => {
        setIsLoading(true);

        // Update order status logic here
        const updatedOrder = await updateOrder(orderData);
        if (updatedOrder) {
            setOrderData(updatedOrder);
        }
        else
            console.log("Error updating order");

        setIsLoading(false);
        setIsEditing(false);
    };

    const handleCancel = () => {
        setOrderData({ ...order });
        setIsEditing(false);
    };

    const handleStatusChange = (event) => {
        setOrderData({ ...orderData, status: event.target.value });
    };

    const DeliverTypeBlock = ({ deliveryType }) => {
        const { backgroundColor, borderColor, textColor, text } = getDeliveryTypeSetup(deliveryType, theme);

        return (
            <Grid
                item
                sx={{
                    display: "inline-block",
                    border: 1,
                    borderColor: borderColor,
                    backgroundColor: backgroundColor,
                    borderRadius: 2,
                    padding: 1.5,
                    paddingY: 0.5,
                    marginTop: 1,
                }}
            >
                <Typography
                    color={textColor}
                    fontSize={11}
                    fontWeight={"bold"}
                    letterSpacing={0.5}
                    textAlign={"center"}
                    fontFamily={"primaryFont"}
                >{`${text}`}</Typography>
            </Grid>
        );
    };

    const OrderItems = ({ orderItems }) => {
        return (
            <Grid
                item
                xs={12}
                marginY={{ lg: 4 }}
            >
                {/* Render order items here */}
                {orderItems.map((item, index) => (
                    // convert to a component called OrderItemCard
                    <OrderItemCard
                        key={`orderItemCard_${index}`}
                        item={item}
                        showRemoveButton={false}
                    />
                ))}
            </Grid>
        );
    };

    const OrderSummary = ({ orderItems, deliveryFee }) => {
        const { baseCost, vat, totalCost } = getOrderPaymentDetails(orderItems, deliveryFee);

        return (
            <Grid item xs={12} mb={2}>
                <Typography
                    letterSpacing={0.5}
                    fontFamily={"primaryFont"}
                    fontSize={16}
                    fontWeight={"bold"}
                >
                    Order Summary
                </Typography>
                <Grid container justifyContent="space-between" mt={1}>
                    <Typography color={theme.palette.text.secondary} fontSize={12} letterSpacing={0.5} fontFamily={"primaryFont"}>
                        Order Cost:
                    </Typography>
                    <Typography color={theme.palette.text.secondary} fontSize={12} letterSpacing={0.5} fontFamily={"primaryFont"}>
                        R {baseCost.toFixed(2)}
                    </Typography>
                </Grid>
                <Grid container justifyContent="space-between">
                    <Typography color={theme.palette.text.secondary} fontSize={12} letterSpacing={0.5} fontFamily={"primaryFont"}>
                        Delivery Fee:
                    </Typography>
                    <Typography color={theme.palette.text.secondary} fontSize={12} letterSpacing={0.5} fontFamily={"primaryFont"}>
                        R {deliveryFee}.00
                    </Typography>
                </Grid>
                <Grid container justifyContent="space-between">
                    <Typography color={theme.palette.text.secondary} fontSize={12} letterSpacing={0.5} fontFamily={"primaryFont"}>
                        VAT ({config.vatPercentage}%):
                    </Typography>
                    <Typography color={theme.palette.text.secondary} fontSize={12} letterSpacing={0.5} fontFamily={"primaryFont"}>
                        R {vat.toFixed(2)}
                    </Typography>
                </Grid>
                <Grid container justifyContent="space-between" mt={1} mb={2}>
                    <Typography
                        color={theme.palette.text.primary}
                        fontSize={16}
                        letterSpacing={0.5}
                        fontFamily={"primaryFont"}
                        fontWeight="bold"
                    >
                        Total:
                    </Typography>
                    <Typography
                        color={theme.palette.text.primary}
                        fontSize={16}
                        letterSpacing={0.5}
                        fontFamily={"primaryFont"}
                        fontWeight="bold"
                    >
                        R {totalCost.toFixed(2)}
                    </Typography>
                </Grid>
                <Divider />
            </Grid>
        );
    };

    return (
        <TableRow>
            <TableCell
                style={{
                    paddingBottom: 0,
                    paddingTop: 0,
                    backgroundColor: theme.palette.mode === "dark"
                        ? theme.palette.background.default
                        : theme.palette.grey[100]
                }}
                colSpan={6}
            >
                <Collapse in={open} timeout="auto" unmountOnExit sx={{ borderBottom: 2, borderColor: config.primaryColor }}>
                    <Box sx={{ marginX: { xs: 1, sm: 2 }, marginY: { xs: 2, sm: 4 }, padding: 1 }}>
                        {/* Action Buttons */}
                        <Box 
                            sx={{ 
                                display: 'flex', 
                                justifyContent: 'flex-end', 
                                alignItems: 'center',
                                gap: 1,
                                mb: { xs: 2, sm: 0 }
                            }}
                        >
                            {isEditing ? (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <BasicButton
                                        title='Save'
                                        variant="contained"
                                        onClick={handleSave}
                                        startIcon={<DoneRounded />}
                                        sx={{ 
                                            height: 36,
                                            fontSize: 12,
                                            px: 2
                                        }}
                                        style={{ backgroundColor: theme.palette.success.main }}
                                        isLoading={isLoading}
                                    />
                                    <BasicButton
                                        title='Cancel'
                                        variant="outlined"
                                        onClick={handleCancel}
                                        startIcon={<Close />}
                                        sx={{
                                            height: 36,
                                            fontSize: 12,
                                            px: 2,
                                            backgroundColor: theme.palette.background.paper,
                                            borderColor: theme.palette.divider
                                        }}
                                    />
                                </Box>
                            ) : (
                                <BasicButton
                                    title='Edit'
                                    variant="outlined"
                                    onClick={handleEdit}
                                    startIcon={<Edit />}
                                    sx={{
                                        height: 36,
                                        fontSize: 12,
                                        px: 2,
                                        backgroundColor: theme.palette.background.paper,
                                        borderColor: theme.palette.primary.main,
                                        '&:hover': {
                                            backgroundColor: theme.palette.primary.main + '10'
                                        }
                                    }}
                                />
                            )}
                        </Box>
                        <Grid container spacing={2.5} mt={{ xs: 1, sm: 0 }}>
                            {/*  Left-Side  */}
                            <Grid item xs={12} md={5}>
                                <Grid container spacing={2.5}>
                                    <Grid item xs={12}>
                                        <Typography fontSize={16} fontWeight={"bold"} fontFamily={"primaryFont"}>
                                            Order Status
                                        </Typography>
                                        <Select
                                            sx={{ paddingX: 2, fontSize: 12, marginTop: 2 }}
                                            value={orderData.status}
                                            onChange={handleStatusChange}
                                            variant="outlined"
                                            disabled={!isEditing}
                                            size="small"
                                        >
                                            <MenuItem value={1} sx={{ fontSize: 12 }}>
                                                <OrderStatusBadgeCompact status={1} />
                                            </MenuItem>
                                            <MenuItem value={2} sx={{ fontSize: 12 }}>
                                                <OrderStatusBadgeCompact status={2} />
                                            </MenuItem>
                                            <MenuItem value={3} sx={{ fontSize: 12 }}>
                                                <OrderStatusBadgeCompact status={3} />
                                            </MenuItem>
                                            <MenuItem value={4} sx={{ fontSize: 12 }}>
                                                <OrderStatusBadgeCompact status={4} />
                                            </MenuItem>
                                            <MenuItem value={5} sx={{ fontSize: 12 }}>
                                                <OrderStatusBadgeCompact status={5} />
                                            </MenuItem>
                                            <MenuItem value={6} sx={{ fontSize: 12 }}>
                                                <OrderStatusBadgeCompact status={6} />
                                            </MenuItem>
                                            <MenuItem value={7} sx={{ fontSize: 12 }}>
                                                <OrderStatusBadgeCompact status={7} />
                                            </MenuItem>
                                            <MenuItem value={8} sx={{ fontSize: 12 }}>
                                                <OrderStatusBadgeCompact status={8} />
                                            </MenuItem>
                                        </Select>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography fontSize={16} fontWeight={"bold"} fontFamily={"primaryFont"} letterSpacing={0.5}>
                                            Delivery Details
                                        </Typography>
                                        <DeliverTypeBlock deliveryType={orderData.deliveryType} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography fontSize={16} fontWeight={"bold"} fontFamily={"primaryFont"} letterSpacing={0.5}>
                                            Shipping Details
                                        </Typography>
                                        {orderData.shippingDetails &&
                                            Object.entries(orderData.shippingDetails).map(([key, value]) => (
                                                <Typography
                                                    color={theme.palette.text.secondary}
                                                    fontSize={14}
                                                    key={key}
                                                >
                                                    {`${value}`}
                                                </Typography>
                                            ))}
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography fontSize={16} fontWeight={"bold"} fontFamily={"primaryFont"} letterSpacing={0.5}>
                                            Contact Details
                                        </Typography>
                                        <Typography color={theme.palette.text.secondary} fontSize={14}>
                                            {orderData.contactDetails.firstName} {orderData.contactDetails.lastName}
                                        </Typography>
                                        {Object.entries(orderData.contactDetails)
                                            .filter(([key]) => key !== "firstName" && key !== "lastName")
                                            .map(([key, value]) => (
                                                <Typography
                                                    color={theme.palette.text.secondary}
                                                    fontSize={14}
                                                    key={key}
                                                >
                                                    {`${value}`}
                                                </Typography>
                                            ))}
                                    </Grid>

                                    {/* Refund Requests Section */}
                                    {refundRequests.length > 0 && (
                                        <Grid item xs={12}>
                                            <Divider sx={{ my: 1 }} />
                                            <Typography fontSize={16} fontWeight={"bold"} fontFamily={"primaryFont"} letterSpacing={0.5} sx={{ mb: 1 }}>
                                                Refund Requests
                                            </Typography>
                                            {refundRequests.map((refund: any) => (
                                                <Box
                                                    key={refund.id}
                                                    sx={{
                                                        p: 1.5,
                                                        mb: 1,
                                                        borderRadius: 2,
                                                        border: `1px solid ${theme.palette.divider}`,
                                                        backgroundColor: theme.palette.background.paper
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                        <Typography fontSize={12} fontWeight={600}>
                                                            {refund.type === 'full' ? 'Full Refund' : 'Partial Refund'}
                                                        </Typography>
                                                        <Typography fontSize={12} fontWeight={700}>
                                                            R{Number(refund.amount).toFixed(2)}
                                                        </Typography>
                                                    </Box>
                                                    <Typography fontSize={11} color="text.secondary" sx={{ mb: 0.5 }}>
                                                        Reason: {refund.reason?.replace(/_/g, ' ')}
                                                    </Typography>
                                                    {refund.customerNote && (
                                                        <Typography fontSize={11} color="text.secondary" sx={{ mb: 0.5 }}>
                                                            Note: {refund.customerNote}
                                                        </Typography>
                                                    )}
                                                    <Alert
                                                        severity={refund.status === 'pending' ? 'warning' : refund.status === 'processed' ? 'success' : refund.status === 'rejected' ? 'error' : 'info'}
                                                        sx={{ py: 0, fontSize: 11, mb: 1 }}
                                                    >
                                                        Status: {refund.status}
                                                    </Alert>

                                                    {refund.status === 'pending' && (
                                                        <Box>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                placeholder="Note to customer (optional)"
                                                                value={merchantNote}
                                                                onChange={(e) => setMerchantNote(e.target.value)}
                                                                sx={{ mb: 1, '& .MuiInputBase-input': { fontSize: 12 } }}
                                                            />
                                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                                <Button
                                                                    size="small"
                                                                    variant="contained"
                                                                    color="success"
                                                                    startIcon={refundActionLoading === refund.id ? <CircularProgress size={14} color="inherit" /> : <CheckCircle sx={{ fontSize: 14 }} />}
                                                                    onClick={() => handleRefundAction(refund.id, 'approve')}
                                                                    disabled={!!refundActionLoading}
                                                                    sx={{ textTransform: 'none', fontSize: 11, py: 0.5 }}
                                                                >
                                                                    Approve
                                                                </Button>
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    color="error"
                                                                    startIcon={<CancelIcon sx={{ fontSize: 14 }} />}
                                                                    onClick={() => handleRefundAction(refund.id, 'reject')}
                                                                    disabled={!!refundActionLoading}
                                                                    sx={{ textTransform: 'none', fontSize: 11, py: 0.5 }}
                                                                >
                                                                    Reject
                                                                </Button>
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </Box>
                                            ))}
                                        </Grid>
                                    )}
                                </Grid>
                            </Grid>
                            {/*  Right-Side  */}
                            <Grid item xs={12} md={7}>
                                <OrderSummary
                                    orderItems={orderData.order}
                                    deliveryFee={orderData.deliveryFee}
                                />
                                <Box sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mb: 2
                                }}>
                                    <Typography
                                        fontSize={{ xs: 14, sm: 16 }}
                                        fontWeight={"bold"}
                                        letterSpacing={0.5}
                                        fontFamily={"primaryFont"}
                                        pr={1}
                                    >
                                        {`${orderData.order?.length || 0} Items Purchased`}
                                    </Typography>
                                    <BasicButton
                                        onClick={() => setExpandItems(!expandItems)}
                                        startIcon={expandItems ? <ExpandLess /> : <ExpandMore />}
                                        title={expandItems ? "Collapse" : "Expand"}
                                        variant="outlined"
                                        sx={{
                                            height: 32,
                                            fontSize: 11,
                                            px: 1.5,
                                            borderColor: theme.palette.divider,
                                            backgroundColor: theme.palette.background.paper,
                                            '&:hover': {
                                                backgroundColor: theme.palette.action.hover
                                            }
                                        }}
                                    />
                                </Box>
                                <Box sx={{
                                    display: expandItems ? "flex" : "none",
                                    maxHeight: { xs: "300px", md: "340px" },
                                    overflow: "auto",
                                    transition: "max-height 5s ease-in-out",
                                    paddingRight: 1,
                                    paddingLeft: 1,
                                    //show scroll bar below
                                    "&::-webkit-scrollbar": {
                                        width: 6,
                                        borderRadius: 3
                                    },
                                    "&::-webkit-scrollbar-thumb": {
                                        backgroundColor: theme.palette.mode === "dark"
                                            ? theme.palette.grey[700]
                                            : theme.palette.grey[400],
                                    },
                                }}>
                                    <OrderItems orderItems={orderData.order} />
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </Collapse>
            </TableCell>
        </TableRow>
    );
}
